<!--
  Alert wrapper for dual Vue 2/3 compatibility.

  Vue 2 (Vuetify 2): `:value` + `@input`.
  Vue 3 (Vuetify 3): `:model-value` + `@update:modelValue`.

  Vuetify renamed the dismiss-button prop `dismissible` (v2) to `closable` (v3);
  the wrapper translates it so call sites stay version-agnostic.
-->
/*VUE2*/<script lang="ts">
/*VUE2*/  export default { inheritAttrs: false };
/*VUE2*/</script>

<script setup lang="ts">


import { computed, useSlots, useAttrs } from 'vue';
/*VUE2*/ import { VAlert } from 'vuetify/lib';


const isVue2 = Boolean(import.meta.env.VUE_APP_V2);

const props = defineProps<{
  modelValue?: unknown;
  value?: unknown;
}>();

const emit = defineEmits<{
  (e: 'input', val: boolean): void;
  (e: 'update:modelValue', val: boolean): void;
}>();

const attrs = useAttrs();

const vue2Props = computed(() => ({
  ...attrs,
  value: props.modelValue ?? props.value,
}));

// Rename the v2 `dismissible` attr to the v3 `closable` prop on the Vue 3 root.
const vue3Attrs = computed(() => {
  const { dismissible, ...rest } = attrs;
  return dismissible === undefined ? rest : { ...rest, closable: dismissible };
});

function onVue2Input(val: unknown) {
  if (val instanceof Event) { return; }
  const visible = Boolean(val);
  emit('input', visible);
  emit('update:modelValue', visible);
}

function onVue3Update(val: unknown) {
  emit('update:modelValue', Boolean(val));
}

const slots = useSlots();
const passthroughSlotNames = computed(() =>
  Object.keys(slots).filter((name) => name !== 'default'),
);
</script>

<template>
  <VAlert
    v-if="isVue2"
    v-bind="vue2Props"
    @input="onVue2Input"
  >
    <slot />
    <template
      v-for="name in passthroughSlotNames"
      #[name]="data"
    >
      <slot
        :name="name"
        v-bind="data"
      />
    </template>
  </VAlert>

  <VAlert
    v-else
    v-bind="vue3Attrs"
    :model-value="props.modelValue ?? props.value"
    @update:model-value="onVue3Update"
  >
    <slot />
    <template
      v-for="name in passthroughSlotNames"
      #[name]="data"
    >
      <slot
        :name="name"
        v-bind="data"
      />
    </template>
  </VAlert>
</template>
